import React, { useCallback } from 'react'
import { components } from 'react-select'
import { AsyncPaginate } from 'react-select-async-paginate'

import { loadOptionsWrapper } from './helpers'

import {
  MultiValueRemove,
  ClearIndicator,
  DropdownIndicator,
  Option,
} from '../Select/components'
import { customStyles } from '../Select/constants'

import type {
  BaseSelectAsyncComponentType,
  OptionComponentProps,
} from './types'

/** Usage example:
 * import { SelectAsync, shouldLoadMore } from '@/components/UIkit/SelectAsync'
 *
 * const getItemsRequest = async ({text, offset, limit}) => {
 *   const response = await fetch(`api/path/endpoint?text=${text}&offset=${offset}&limit=${limit}`)
 *   return response.data.items
 * }
 *
 *
 * const LOAD_ITEMS_LIMIT = 20
 * const requestWrapper = (request) => async (params) => {
 *   const response = await request({
 *     text: params.search,
 *     offset: params.offset,
 *     limit: LOAD_ITEMS_LIMIT,
 *   })
 *
 *   const hasMore = response.data.total > params.offset + LOAD_ITEMS_LIMIT
 *   const options = response.data.items.map((item) => ({ label: item.id,  value: item.title }))
 *   return {
 *     hasMore,
 *     options,
 *   }
 * }
 *
 * const SelectBox: React.FC = (props) => {
 *   return (
 *     <SelectAsync
 *       placeholder="My async select"
 *       {...props}
 *       request={requestWrapper(getItemsRequest)}
 *       shouldLoadMore={shouldLoadMore} // optional
 *     />
 *   )
 * }
 * */
const IndicatorSeparator = () => <span />

export const SelectAsync: BaseSelectAsyncComponentType = (props) => {
  const {
    isMulti,
    closeMenuOnSelect = !isMulti,
    dataCy,
    hideSelectedOptions = false,
    extraComponents,
    ...restProps
  } = props
  const Control = useCallback(
    (props) => (
      <components.Control
        {...props}
        innerProps={{
          'data-cy': dataCy,
          ...props.innerProps,
        }}
      />
    ),
    [dataCy]
  )

  return (
    <AsyncPaginate
      components={{
        DropdownIndicator,
        MultiValueRemove,
        ClearIndicator,
        IndicatorSeparator,
        Control,
        Option: (props: OptionComponentProps) => Option(props),
        ...extraComponents,
      }}
      classNamePrefix="react-select"
      hideSelectedOptions={hideSelectedOptions}
      loadOptions={loadOptionsWrapper(props.request)}
      styles={customStyles}
      isMulti={isMulti}
      closeMenuOnSelect={closeMenuOnSelect}
      {...restProps}
      value={props?.value || null}
    />
  )
}
